import { AfterViewInit, Component, OnInit, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute } from "@angular/router";
import { faDownload, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { minBy } from "lodash";
import { Subscription } from "rxjs";
import { rowExpandAnimation } from "src/app/animations/row-expand-animation";
import LocalizationConstants from "src/app/constants/localization-constants";
import { Column } from "src/app/models/Column";
import { Company } from "src/app/models/Company";
import { DocType } from "src/app/models/Enums";
import { Event as FieldServiceEvent } from "src/app/models/Event";
import { EventsByOrder } from "src/app/models/EventsByOrder";
import { CompanyService } from "src/app/services/company.service";
import { ToolService } from "src/app/services/tool.service";

import { FieldServiceService } from "./../../services/field-service.service";

@Component({
  selector: "app-field-service-history",
  templateUrl: "./field-service-history.component.html",
  styleUrls: ["./field-service-history.component.scss"],
  animations: [rowExpandAnimation],
  standalone: false,
})
export class FieldServiceHistoryComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  readonly localizationConstants = LocalizationConstants;
  faDownload = faDownload;
  faSpinner = faSpinner;
  subscription: Subscription;
  company: Company;
  dataSource = new MatTableDataSource<EventsByOrder>([]);
  companyId: string;
  jobNumber: string;
  status: string; // planned or last job, need to fetch correct details
  pageSize: number = 10;
  isLoading: boolean;

  eventColumns: Column[] = [
    { key: "name", title: "toolhistory.event" },
    { key: "date", title: "toolhistory.completed" },
    { key: "docs", title: "toolhistory.documentation" },
  ];

  eventColumnKeys = this.eventColumns.map((column) => column.key);

  constructor(
    public fsService: FieldServiceService,
    private companyService: CompanyService,
    private route: ActivatedRoute,
    private toolService: ToolService,
  ) {}

  ngOnInit() {
    this.companyId = this.route.parent.snapshot.paramMap.get("companyId");
    this.jobNumber = this.route.parent.snapshot.paramMap.get("jobNumber");
    this.status = this.route.parent.snapshot.paramMap.get("status");
    this.company = this.companyService.selectedCompany;

    if (!this.company?.name || this.company.name != this.companyId) return;
    this.getFsJobHistory();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  getFsJobHistory() {
    this.isLoading = true;
    this.toolService.getToolDetails(this.companyId, this.jobNumber).subscribe(
      (response) => {
        this.isLoading = false;
        if (!response) return;

        this.dataSource.data = response.eventsByRaNumber;
        this.dataSource.data.forEach((x) => {
          x.flowType = this.getFlowType(x.raNumber);
          x.startDate = minBy(x.events, (e) => e.date).date;
          x.expanded = true;
          x.events.forEach((e) => {
            e.docs = [];
            e.documents.forEach((d) => {
              (d.documentType = DocType.EventDocument), e.docs.push(d);
            });
            e.certificates.forEach((c) => {
              (c.documentType = DocType.Certificate), e.docs.push(c);
            });
          });
        });
      },
      () => (this.isLoading = false),
    );
  }

  getFlowType(raNumber: string) {
    if (
      raNumber?.startsWith("FSESI") ||
      (raNumber?.startsWith("FS") && !raNumber?.startsWith("FSE"))
    )
      return "toolhistory.fieldserviceflow";

    return "toolhistory.workshopflow";
  }

  public isExpansionDetailRow(index: number, job: EventsByOrder): boolean {
    return job.events.length > 0;
  }

  public getDataSource(events: FieldServiceEvent[]): MatTableDataSource<FieldServiceEvent> {
    return new MatTableDataSource<FieldServiceEvent>(events);
  }

  public getEventsDataSource(
    events: FieldServiceEvent[],
    matSort: MatSort,
  ): MatTableDataSource<FieldServiceEvent> {
    const dataSource = this.getDataSource(events);
    dataSource.sort = matSort;
    return dataSource;
  }

  public toggleExpandRow(row: EventsByOrder): void {
    row.expanded = !row.expanded;
  }
}
